import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react"

// --- State ---

export interface CustomerSiteSelectorState {
  customerId: string | null
  customerName: string | null
  siteId: string | null
  siteName: string | null
}

const initialState: CustomerSiteSelectorState = {
  customerId: null,
  customerName: null,
  siteId: null,
  siteName: null,
}

// --- Actions ---

export interface CustomerSiteSelectorActions {
  setCustomer: (customerId: string | null, customerName?: string | null) => void
  setSite: (siteId: string | null, siteName?: string | null) => void
  reset: () => void
}

// --- System name (isolated to avoid re-renders of customer/site selectors) ---

interface SystemNameContextValue {
  systemName: string
  setSystemName: (name: string) => void
}

// --- Contexts (split to prevent unnecessary re-renders) ---

export const CustomerSiteSelectorStateContext = createContext<CustomerSiteSelectorState | undefined>(undefined)
export const CustomerSiteSelectorActionsContext = createContext<CustomerSiteSelectorActions | undefined>(undefined)
const SystemNameContext = createContext<SystemNameContextValue | undefined>(undefined)

// --- Provider ---

export interface CustomerSiteSelectorProviderProps {
  children: ReactNode
  /** Pre-set customer ID — locks the customer selection */
  initialCustomerId?: string | null
  initialCustomerName?: string | null
  /** Pre-set site ID — locks the site selection */
  initialSiteId?: string | null
  initialSiteName?: string | null
}

export const CustomerSiteSelectorProvider: React.FC<CustomerSiteSelectorProviderProps> = ({
  children,
  initialCustomerId = null,
  initialCustomerName = null,
  initialSiteId = null,
  initialSiteName = null,
}) => {
  const [state, setState] = useState<CustomerSiteSelectorState>({
    customerId: initialCustomerId,
    customerName: initialCustomerName,
    siteId: initialSiteId,
    siteName: initialSiteName,
  })

  const [systemName, setSystemNameRaw] = useState("")

  const setCustomer = useCallback((customerId: string | null, customerName?: string | null) => {
    setState({
      customerId,
      customerName: customerName ?? null,
      siteId: null,
      siteName: null,
    })
  }, [])

  const setSite = useCallback((siteId: string | null, siteName?: string | null) => {
    setState((prev) => ({
      ...prev,
      siteId,
      siteName: siteName ?? null,
    }))
  }, [])

  const reset = useCallback(() => {
    setState({
      customerId: initialCustomerId,
      customerName: initialCustomerName,
      siteId: initialSiteId,
      siteName: initialSiteName,
    })
    setSystemNameRaw("")
  }, [initialCustomerId, initialCustomerName, initialSiteId, initialSiteName])

  const actions = useMemo<CustomerSiteSelectorActions>(
    () => ({ setCustomer, setSite, reset }),
    [setCustomer, setSite, reset],
  )

  const systemNameValue = useMemo<SystemNameContextValue>(
    () => ({ systemName, setSystemName: setSystemNameRaw }),
    [systemName],
  )

  return (
    <CustomerSiteSelectorStateContext.Provider value={state}>
      <CustomerSiteSelectorActionsContext.Provider value={actions}>
        <SystemNameContext.Provider value={systemNameValue}>
          {children}
        </SystemNameContext.Provider>
      </CustomerSiteSelectorActionsContext.Provider>
    </CustomerSiteSelectorStateContext.Provider>
  )
}

// --- Hooks ---

export function useCustomerSiteSelectorState(): CustomerSiteSelectorState {
  const context = useContext(CustomerSiteSelectorStateContext)
  if (!context) throw new Error("useCustomerSiteSelectorState must be used within CustomerSiteSelectorProvider")
  return context
}

export function useCustomerSiteSelectorActions(): CustomerSiteSelectorActions {
  const context = useContext(CustomerSiteSelectorActionsContext)
  if (!context) throw new Error("useCustomerSiteSelectorActions must be used within CustomerSiteSelectorProvider")
  return context
}

/**
 * Isolated hook for system name — only re-renders the subscribing component,
 * not the customer/site selector or parent dialog.
 */
export function useSystemName(): SystemNameContextValue {
  const context = useContext(SystemNameContext)
  if (!context) throw new Error("useSystemName must be used within CustomerSiteSelectorProvider")
  return context
}
